import { cx } from '@/lib/utils'
import Link from 'next/link'
import { forwardRef } from 'react'

type Props =
    | ({
          href: string
          target?: string
          rel?: string
      } & CommonProps)
    | ({ href?: never } & CommonProps)

type CommonProps = {
    children: React.ReactNode
    type?: 'button' | 'submit' | 'reset'
    onClick?: () => void
    disabled?: boolean
    variant?: 'cta' | 'primary' | 'ghostOnDark'
    className?: string
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(function Button(props, ref) {
    const variant = props.variant ?? 'primary'

    const base =
        'relative inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold ' +
        'tracking-wide shadow-sm transition-[transform,box-shadow] duration-200 ' +
        'hover:shadow-md focus-visible:shadow-md disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer ' +
        'select-none'

    const styles =
        variant === 'cta'
            ? // Premium CTA: clean gradient + subtle top highlight (no weird sweep)
              'text-white shadow-md hover:shadow-lg ' +
              'bg-linear-to-r from-[color-mix(in_oklab,var(--color-accent)_92%,#000)] to-[color-mix(in_oklab,var(--color-accent-2)_80%,#000)] ' +
              'before:pointer-events-none before:absolute before:inset-0 before:rounded-lg ' +
              'before:bg-linear-to-b before:from-white/18 before:to-white/0 before:opacity-100'
            : variant === 'ghostOnDark'
              ? 'bg-white/10 text-on-dark hover:shadow-md'
              : 'bg-dark text-on-dark hover:shadow-md'

    if ('href' in props && props.href) {
        return (
            <Link
                ref={ref as never}
                href={props.href}
                target={props.target}
                rel={props.rel}
                className={cx(base, styles, props.className)}
                aria-disabled={props.disabled ? 'true' : undefined}
                tabIndex={props.disabled ? -1 : 0}>
                {props.children}
            </Link>
        )
    }

    return (
        <button
            ref={ref as never}
            type={props.type ?? 'button'}
            onClick={props.onClick}
            disabled={props.disabled}
            className={cx(base, styles, props.className)}>
            {props.children}
        </button>
    )
})

export default Button
